import { useParams, Navigate } from 'react-router-dom'
import SEO from '../components/SEO'
import ScrollReveal from '../components/ScrollReveal'
import { getPostBySlug } from '../news'
import './NewsPost.css'
import '../styles/shared-hero.css'

function NewsPost() {
  const { slug } = useParams<{ slug: string }>()
  const post = slug ? getPostBySlug(slug) : undefined

  if (!post) {
    return <Navigate to="/news" replace />
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Parse bold text (**text**) in a string
  const parseBoldText = (text: string): (string | JSX.Element)[] => {
    const parts = text.split(/(\*\*.*?\*\*)/g)
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>
      }
      return part
    })
  }

  // Simple markdown-like content renderer
  const renderContent = (content: string) => {
    const lines = content.trim().split('\n')
    const elements: JSX.Element[] = []
    let inList = false
    let listItems: string[] = []

    const flushList = () => {
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="blog-content-list">
            {listItems.map((item, idx) => (
              <li key={idx}>{parseBoldText(item.replace(/^\d+\.\s*/, '').replace(/^-\s*/, ''))}</li>
            ))}
          </ul>
        )
        listItems = []
        inList = false
      }
    }

    lines.forEach((line, index) => {
      const trimmedLine = line.trim()

      if (trimmedLine.startsWith('# ')) {
        flushList()
        elements.push(
          <h1 key={index} className="blog-content-h1">
            {parseBoldText(trimmedLine.replace('# ', ''))}
          </h1>
        )
      } else if (trimmedLine.startsWith('## ')) {
        flushList()
        elements.push(
          <h2 key={index} className="blog-content-h2">
            {parseBoldText(trimmedLine.replace('## ', ''))}
          </h2>
        )
      } else if (trimmedLine.startsWith('### ')) {
        flushList()
        elements.push(
          <h3 key={index} className="blog-content-h3">
            {parseBoldText(trimmedLine.replace('### ', ''))}
          </h3>
        )
      } else if (trimmedLine.startsWith('- ') || /^\d+\.\s/.test(trimmedLine)) {
        inList = true
        listItems.push(trimmedLine)
      } else if (trimmedLine === '') {
        flushList()
      } else {
        flushList()
        elements.push(
          <p key={index} className="blog-content-paragraph">
            {parseBoldText(trimmedLine)}
          </p>
        )
      }
    })

    flushList()
    return elements
  }

  return (
    <>
      <SEO 
        title={`${post.title} | News`} 
        description={post.excerpt}
        path={`/news/${post.slug}`}
      />
      
      <article className="blog-post-page">
        {/* Header */}
        <header className="blog-post-header page-hero">
          <div className="page-hero-content blog-post-header-content">
            <ScrollReveal>
              <div className="blog-post-header-meta">
                <time dateTime={post.date}>{formatDate(post.date)}</time>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <h1 className="blog-post-header-title">{post.title}</h1>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p className="blog-post-header-excerpt">{post.excerpt}</p>
            </ScrollReveal>
          </div>
        </header>

        {/* Content */}
        <div className="blog-post-content-wrapper">
          <ScrollReveal className="blog-post-content">
            {renderContent(post.content)}
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <div className="blog-post-footer">
              <div className="blog-post-tags-detail">
                {post.tags.map((tag) => (
                  <span key={tag} className="blog-post-tag-detail">{tag}</span>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </article>
    </>
  )
}

export default NewsPost
